/**
 * Each playable city's winter cold on the game's representative days, so the extreme-cold hazard
 * is judged against local norms rather than one global number (issue #73). Places that routinely
 * drop below a standard gas plant's rating build for their own winters, and regional gas supply
 * only strains in cold that is rare for the place.
 *
 * Derived from the historical weather in public/data/weather (1980-2024) with
 * coldClimateFromAnnualMinima: the coldest representative-day hour of each calendar year, then
 * - routineLowC: the low reached in about one winter in four;
 * - rareLowC: the low reached in about one winter in twelve.
 * `src/testing/ColdClimate.test.tsx` recomputes both from the weather files and prints the
 * replacement table if the weather is regenerated.
 */

/** [routineLowC, rareLowC] in whole °C, by location id. */
export type ColdClimateRow = readonly [routineLowC: number, rareLowC: number];

/** Historical years the table is derived from. */
export const COLD_CLIMATE_YEARS = { first: 1980, last: 2024 };

/** The two statistics from each year's coldest representative-day reading. */
export function coldClimateFromAnnualMinima(minima: number[]): ColdClimateRow {
  const sorted = [...minima].sort((a, b) => a - b);
  const n = sorted.length;
  return [
    Math.round(sorted[Math.floor(n / 4)]),
    Math.floor(sorted[Math.floor(n / 12)]),
  ];
}

export const COLD_CLIMATE_BY_LOCATION: Readonly<
  Record<string, ColdClimateRow>
> = {
  PIT: [-13, -19],
  SF: [3, 2],
  LA: [2, -1],
  CAMountains: [-4, -6],
  HNL: [19, 18],
  SJU: [20, 19],
  NewYork: [-12, -16],
  Chicago: [-16, -19],
  Houston: [2, 0],
  Phoenix: [1, -2],
  Philadelphia: [-12, -15],
  SanAntonio: [2, 0],
  SanDiego: [6, 3],
  Dallas: [-1, -3],
  Austin: [0, -2],
  Manassas: [-9, -15],
  Jacksonville: [4, 0],
  Columbus: [-15, -21],
  Indianapolis: [-14, -21],
  Charlotte: [-4, -9],
  Seattle: [-3, -5],
  Denver: [-17, -24],
  Boston: [-17, -21],
  Detroit: [-16, -22],
  Nashville: [-7, -12],
  Portland: [-3, -5],
  LasVegas: [-1, -5],
  Memphis: [-4, -7],
  Baltimore: [-9, -13],
  Milwaukee: [-15, -20],
  Albuquerque: [-7, -9],
  Tucson: [0, -3],
  Fresno: [1, -2],
  Sacramento: [1, -2],
  KansasCity: [-12, -18],
  Atlanta: [-4, -6],
  Miami: [12, 9],
  Minneapolis: [-23, -29],
  NewOrleans: [5, 2],
  SaltLakeCity: [-14, -16],
  Anchorage: [-22, -25],
  Fairbanks: [-34, -42],
  StLouis: [-12, -17],
  Cleveland: [-14, -17],
  Buffalo: [-16, -19],
  Toronto: [-16, -22],
  Montreal: [-25, -29],
  Vancouver: [-3, -6],
  Calgary: [-22, -31],
  Winnipeg: [-32, -37],
  Halifax: [-14, -20],
  Yellowknife: [-37, -41],
  Iqaluit: [-37, -41],
  MexicoCity: [4, 1],
  Guadalajara: [7, 5],
  Monterrey: [6, 3],
  Tijuana: [6, 3],
  Cancun: [20, 18],
  Havana: [17, 14],
  SantoDomingo: [19, 18],
  PortAuPrince: [19, 16],
  Kingston: [20, 19],
  Nassau: [18, 16],
  Bridgetown: [21, 20],
  GuatemalaCity: [11, 9],
  SanSalvador: [17, 14],
  Tegucigalpa: [14, 12],
  Managua: [22, 20],
  SanJoseCR: [15, 14],
  PanamaCity: [23, 22],
  Bogota: [9, 6],
  Medellin: [14, 13],
  Caracas: [18, 16],
  Quito: [6, 5],
  Guayaquil: [20, 19],
  Lima: [14, 13],
  LaPaz: [-1, -2],
  Santiago: [0, -4],
  BuenosAires: [3, 1],
  Cordoba: [1, -1],
  Mendoza: [0, -3],
  Ushuaia: [-7, -10],
  Montevideo: [5, 4],
  Asuncion: [8, 5],
  SaoPaulo: [9, 7],
  RioDeJaneiro: [15, 13],
  Brasilia: [13, 11],
  Salvador: [21, 20],
  Fortaleza: [23, 22],
  Manaus: [23, 22],
  PortoAlegre: [5, 3],
  Recife: [22, 21],
  Belem: [23, 22],
  Georgetown: [23, 23],
  Paramaribo: [23, 22],
  London: [-2, -5],
  Manchester: [-3, -5],
  Edinburgh: [-2, -4],
  Dublin: [0, -1],
  Paris: [-4, -8],
  Marseille: [1, -2],
  Lyon: [-4, -9],
  Madrid: [-3, -4],
  Barcelona: [1, -1],
  Seville: [3, 0],
  Lisbon: [6, 3],
  Porto: [3, 0],
  Rome: [0, -3],
  Milan: [-4, -5],
  Naples: [1, -1],
  Palermo: [6, 4],
  Berlin: [-8, -12],
  Munich: [-12, -14],
  Hamburg: [-8, -13],
  Frankfurt: [-7, -12],
  Cologne: [-6, -8],
  Vienna: [-8, -12],
  Zurich: [-7, -10],
  Geneva: [-6, -10],
  Amsterdam: [-3, -6],
  Rotterdam: [-4, -7],
  Brussels: [-4, -12],
  Copenhagen: [-5, -9],
  Oslo: [-15, -19],
  Bergen: [-9, -12],
  Tromso: [-19, -27],
  Stockholm: [-10, -16],
  Gothenburg: [-10, -16],
  Helsinki: [-15, -19],
  Reykjavik: [-11, -14],
  Warsaw: [-11, -15],
  Krakow: [-12, -18],
  Prague: [-10, -17],
  Budapest: [-9, -15],
  Bucharest: [-9, -16],
  Sofia: [-11, -14],
  Belgrade: [-7, -12],
  Zagreb: [-9, -15],
  Athens: [2, -1],
  Kyiv: [-14, -21],
  Minsk: [-16, -21],
  Riga: [-14, -17],
  Vilnius: [-14, -19],
  Tallinn: [-15, -18],
  Moscow: [-21, -28],
  StPetersburg: [-18, -26],
  Murmansk: [-22, -25],
  Istanbul: [0, -4],
  Ankara: [-7, -14],
  Dubai: [12, 10],
  AbuDhabi: [13, 10],
  Doha: [11, 8],
  Riyadh: [3, 0],
  Jeddah: [18, 16],
  KuwaitCity: [7, 4],
  Manama: [12, 10],
  Muscat: [15, 13],
  Baghdad: [3, 0],
  Tehran: [-10, -16],
  Amman: [2, -1],
  Beirut: [5, 3],
  Damascus: [-2, -5],
  TelAviv: [8, 6],
  Jerusalem: [3, 0],
  Baku: [1, -2],
  Tbilisi: [-13, -16],
  Yerevan: [-12, -16],
  Tashkent: [-14, -17],
  Almaty: [-14, -17],
  Astana: [-29, -32],
  Kabul: [-14, -19],
  Cairo: [6, 4],
  Alexandria: [9, 8],
  Casablanca: [6, 4],
  Marrakesh: [0, -2],
  Algiers: [4, 2],
  Tunis: [5, 3],
  Tripoli: [8, 5],
  Lagos: [22, 20],
  Abuja: [20, 19],
  Kano: [11, 10],
  Accra: [22, 21],
  Abidjan: [22, 20],
  Dakar: [18, 16],
  Bamako: [17, 15],
  Ouagadougou: [15, 14],
  Niamey: [13, 12],
  Khartoum: [12, 10],
  AddisAbaba: [5, 4],
  Nairobi: [11, 10],
  Mombasa: [22, 21],
  Kampala: [16, 15],
  Kigali: [13, 12],
  DarEsSalaam: [21, 20],
  Kinshasa: [17, 16],
  Luanda: [16, 15],
  Lusaka: [7, 6],
  Harare: [6, 5],
  Maputo: [13, 12],
  Gaborone: [3, 2],
  Windhoek: [2, -2],
  Johannesburg: [0, -2],
  CapeTown: [6, 4],
  Durban: [12, 11],
  Antananarivo: [8, 7],
  PortLouis: [19, 17],
  Delhi: [6, 4],
  Mumbai: [18, 16],
  Bengaluru: [13, 12],
  Chennai: [21, 20],
  Kolkata: [11, 10],
  Hyderabad: [14, 12],
  Ahmedabad: [11, 10],
  Beijing: [-11, -14],
};
